import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link, useParams } from "react-router-dom";
import { ArrowLeft, Save, Plus, Trash2 } from "lucide-react";
import { handleUpdate, handleGet } from "../../../controllers/controller.all";
import SubmitButton from "../../../components/ui/SubmitButton";


export default function StudentUpdate() {
    const { id } = useParams();
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        nom: "",
        prenom: "",
        email: "",
        tel: "",
        adresse: "",
        classe: "",
        genre: "",
        date_naissance: "",
        tuteurs: [],
        inscriptions: []
    });

    const [classes, setClasses] = useState([]);
    const [originalClasse, setOriginalClasse] = useState("");
    const [activeAnnee, setActiveAnnee] = useState("");

    useEffect(() => {
        const fetchRefs = async () => {
            // Fetch classes
            await handleGet(`/classe/ecole/${ecoleId}`, (res) => {
                if (res?.classes) {
                    const ecoleClasses = res.classes.filter(c => c.ecole === ecoleId || c.ecole?._id === ecoleId);
                    setClasses(ecoleClasses);
                }
            }, token);

            // Fetch active year
            await handleGet(`/ecole-annee/ecole/${ecoleId}/active`, (res) => {
                if (res && res.annee) {
                    setActiveAnnee(res.annee);
                }
            }, token);

            // Fetch student data
            await handleGet(`/user/${id}`, (res) => {
                if (res?.user) {
                    const s = res.user;
                    setFormData({
                        nom: s.nom || "",
                        prenom: s.prenom || "",
                        email: s.email || "",
                        tel: s.tel || "",
                        adresse: s.adresse || "",
                        classe: s.classe?._id || s.classe || "",
                        genre: s.genre || "",
                        date_naissance: s.date_naissance || "",
                        tuteurs: s.tuteurs || [],
                        inscriptions: s.inscriptions || []
                    });
                    setOriginalClasse(s.classe?._id || s.classe || "");
                }
            }, token);
        };
        if (token && id) fetchRefs();
    }, [token, id, ecoleId]);

    const handleAddTuteur = () => {
        setFormData({
            ...formData,
            tuteurs: [...formData.tuteurs, { nom: "", prenom: "", tel: "" }]
        });
    };

    const handleRemoveTuteur = (index) => {
        const newTuteurs = [...formData.tuteurs];
        newTuteurs.splice(index, 1);
        setFormData({ ...formData, tuteurs: newTuteurs });
    };

    const handleTuteurChange = (index, field, value) => {
        const newTuteurs = [...formData.tuteurs];
        newTuteurs[index][field] = value;
        setFormData({ ...formData, tuteurs: newTuteurs });
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        const newInscriptions = [...formData.inscriptions];
        if (formData.classe && formData.classe !== originalClasse) {
            const anneeVal = activeAnnee || new Date().getFullYear().toString();
            // Eviter les doublons exacts
            const exists = newInscriptions.find(i => i.annee === anneeVal && i.classe === formData.classe);
            if (!exists) {
                newInscriptions.push({
                    annee: anneeVal,
                    classe: formData.classe,
                    date: new Date().toISOString()
                });
            }
        }

        const payload = {
            ...formData,
            inscriptions: newInscriptions,
            role: "ETUDIANT",
            ecole: ecoleId
        };
        const res = await handleUpdate(e, `/user/${id}`, payload, setLoading, token);
        if (res) {
            navigate("/students");
        }
    };

    return (
        <div className="flex flex-col gap-6 max-w-3xl mx-auto">
            <div className="flex items-center gap-4">
                <Link to="/students" className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition text-gray-500">
                    <ArrowLeft className="h-5 w-5" />
                </Link>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Modifier l'Étudiant</h1>
                    <p className="text-sm text-gray-500">Mettez à jour les informations de l'étudiant.</p>
                </div>
            </div>

            {/* Historique des Inscriptions */}
            {formData.inscriptions.length > 0 && (
                <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-medium text-gray-900">Historique des Inscriptions</h2>
                            <p className="text-sm text-gray-500">Liste des classes fréquentées par l'étudiant au fil des années.</p>
                        </div>
                        <a
                            href={`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/user/${id}/historique-pdf`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-4 py-2 text-sm font-medium border border-indigo-100 rounded-lg hover:bg-indigo-100 transition"
                        >
                            Exporter PDF
                        </a>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Année Scolaire</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Classe</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date d'inscription</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {formData.inscriptions.map((insc, idx) => (
                                    <tr key={idx}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{insc.annee}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {classes.find(c => c._id === insc.classe)?.libelle || insc.classe}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {insc.date ? new Date(insc.date).toLocaleDateString("fr-FR") : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="col-span-1 border-b md:border-b-0 md:col-span-2 pb-4 mb-2 border-gray-200">
                        <h2 className="text-lg font-medium text-gray-900">Informations Personnelles</h2>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Prénom *</label>
                        <input required type="text" name="prenom" value={formData.prenom} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Ex: Jean" />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Nom de famille *</label>
                        <input required type="text" name="nom" value={formData.nom} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Ex: Dupont" />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Email</label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="jean@example.com" />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Téléphone *</label>
                        <input required type="text" name="tel" value={formData.tel} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Ex: +221 77..." />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Adresse</label>
                        <input type="text" name="adresse" value={formData.adresse} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Ex: Dakar, Sénégal" />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Genre</label>
                        <select name="genre" value={formData.genre} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white">
                            <option value="">Non spécifié</option>
                            <option value="M">Masculin</option>
                            <option value="F">Féminin</option>
                        </select>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Date de naissance</label>
                        <input type="date" name="date_naissance" value={formData.date_naissance} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Classe *</label>
                        <select
                            required
                            name="classe"
                            value={formData.classe}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                        >
                            <option value="">Sélectionnez une classe</option>
                            {classes.map(c => (
                                <option key={c._id} value={c._id}>{c.libelle}</option>
                            ))}
                        </select>
                    </div>


                    <div className="col-span-1 md:col-span-2 mt-4 pb-4 mb-2 border-b border-gray-200 flex items-center justify-between">
                        <h2 className="text-lg font-medium text-gray-900">Tuteurs / Responsables</h2>
                        <button
                            type="button"
                            onClick={handleAddTuteur}
                            className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                        >
                            <Plus className="h-4 w-4" />
                            Ajouter un tuteur
                        </button>
                    </div>

                    {formData.tuteurs.length === 0 ? (
                        <div className="col-span-1 md:col-span-2 py-4 text-center border-2 border-dashed border-gray-200 rounded-lg">
                            <p className="text-sm text-gray-500">Aucun tuteur ajouté. Cliquez sur le bouton ci-dessus pour en ajouter un.</p>
                        </div>
                    ) : (
                        formData.tuteurs.map((tuteur, index) => (
                            <div key={index} className="col-span-1 md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200 relative group">
                                <button
                                    type="button"
                                    onClick={() => handleRemoveTuteur(index)}
                                    className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-500 uppercase">Prénom</label>
                                        <input
                                            required
                                            type="text"
                                            value={tuteur.prenom}
                                            onChange={(e) => handleTuteurChange(index, "prenom", e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                                            placeholder="Prénom du tuteur"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-500 uppercase">Nom</label>
                                        <input
                                            required
                                            type="text"
                                            value={tuteur.nom}
                                            onChange={(e) => handleTuteurChange(index, "nom", e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                                            placeholder="Nom du tuteur"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-500 uppercase">Téléphone</label>
                                        <input
                                            required
                                            type="text"
                                            value={tuteur.tel}
                                            onChange={(e) => handleTuteurChange(index, "tel", e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                                            placeholder="Téléphone du tuteur"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <Link to="/students" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                        Annuler
                    </Link>
                    <SubmitButton type="submit" isSubmitting={loading} disabled={loading} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
                        <Save className="h-4 w-4" />
                        {loading ? "Mise à jour..." : "Mettre à jour"}
                    </SubmitButton>
                </div>
            </form>
        </div>
    );
}
